/**
 * The rows a meeting's thread keeps OUT of the chat: what the room said to the agent while the meeting ran,
 * and the turn that answered it.
 *
 * A meeting's conversation is shown as its write-up card (and whatever the owner and Maestro say around it).
 * The room's exchange is still in the conversation, and the agent still reads it, but on screen it reads as a
 * transcript of the call, which the card and `transcript.md` already carry.
 *
 * A room question is a USER row the harness stamped with the meeting's session (`fromMeetingRoom`). Everything
 * after it up to the next USER row the owner wrote, or the write-up card, is that question's answer — except
 * the approval row (the owner's own card) and the reply to an owner message the room question landed in front
 * of: the room's own answers are spoken (`spokenAloud`), the owner's reply is not.
 *
 * Import-free on purpose, so it is testable without the chat's data layer.
 */
export interface MeetingRoomRow {
  role?: string;
  content?: string;
  fromMeetingRoom?: boolean;
  spokenAloud?: boolean;
  meetingNotesCard?: unknown;
  meetingApproval?: unknown;
}

export function withoutMeetingRoomExchange<M extends MeetingRoomRow>(messages: readonly M[]): M[] {
  const kept: M[] = [];
  let inExchange = false;
  let ownerOwed = false;
  for (const message of messages) {
    if (message.role === 'USER') {
      inExchange = message.fromMeetingRoom === true;
      if (!message.fromMeetingRoom) ownerOwed = true;
    } else if (message.meetingNotesCard) {
      inExchange = false;
    }
    if (!inExchange || message.meetingApproval || (ownerOwed && answersOwner(message))) kept.push(message);
    if (ownerOwed && isOwnerAnswer(message)) ownerOwed = false;
  }
  return kept;
}

/** An assistant-side row that is not the room's: not spoken in the room, not the approval ask, not the write-up. */
function answersOwner(message: MeetingRoomRow): boolean {
  return (
    (message.role === 'ASSISTANT' || message.role === 'ASSISTANT_STEP') &&
    !message.spokenAloud &&
    !message.meetingApproval &&
    !message.meetingNotesCard
  );
}

/** The owner's reply itself — a step is the turn still working, and an empty row has not answered yet. */
function isOwnerAnswer(message: MeetingRoomRow): boolean {
  return answersOwner(message) && message.role === 'ASSISTANT' && !!message.content?.trim();
}

/**
 * Is the room's turn the only one in flight? True when the newest USER row is a room question, no write-up card
 * came after it, and no message the owner typed is still waiting for its reply. The room's turn is answered in
 * the meeting, so a turn frame for it must not show the owner a loader; an owner message the room question
 * landed behind still owes one. A chat with no room row is never one. Mirrors iOS and Android
 * `meetingRoomTurnOnly`.
 */
export function meetingRoomTurnOnly(messages: readonly MeetingRoomRow[]): boolean {
  let room = -1;
  let owner = -1;
  let notes = -1;
  messages.forEach((message, i) => {
    if (message.role === 'USER') {
      if (message.fromMeetingRoom) room = i;
      else owner = i;
    }
    if (message.meetingNotesCard) notes = i;
  });
  if (room < 0 || notes > room || owner > room) return false;
  return owner < 0 || messages.slice(owner + 1).some(isOwnerAnswer);
}

/**
 * A meeting's thread whose rows are all room exchange (a live meeting with only public questions and no
 * write-up yet) shows one neutral line instead of a blank transcript. Every other empty chat is unchanged.
 * A room row proves the thread is a meeting's even when the conversation row (and so its channel) is
 * not loaded yet.
 */
export function showsMeetingInProgress(meetingThread: boolean, rows: readonly MeetingRoomRow[], shown: readonly unknown[]): boolean {
  return shown.length === 0 && (meetingThread ? rows.length > 0 : rows.some((row) => row.fromMeetingRoom === true));
}

/** An ASSISTANT row stamped `metadata.voiceTranscript: true` (an object, or a JSON string). Mirrors Android
 *  `parseSpokenAloud` and iOS `ChatCards.spokenAloud`. */
export function readSpokenAloud(role: string | undefined, metadata: unknown): boolean {
  if (role !== 'ASSISTANT') return false;
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata);
    } catch {
      return false;
    }
  }
  return !!metadata && typeof metadata === 'object' && (metadata as { voiceTranscript?: unknown }).voiceTranscript === true;
}

/**
 * Whether a row is a room question: a USER row whose metadata (an object, or a JSON string) carries the
 * meeting's session. The same check as the history route and as Android `parseFromMeetingRoom` / iOS
 * `meetingRoomFlag`.
 */
export function readMeetingRoomFlag(role: string | undefined, metadata: unknown): boolean {
  if (role !== 'USER') return false;
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata);
    } catch {
      return false;
    }
  }
  return !!metadata && typeof metadata === 'object' && typeof (metadata as { meetingSessionId?: unknown }).meetingSessionId === 'string';
}

/**
 * Whether the newest row is the reply that ends the owner's turn on screen. A line spoken in the meeting room
 * and an approval card answer the room, so they never stand the owner's loader down. Mirrors iOS
 * `producedOutput` and Android `assistantAnswerOnScreen`.
 */
export function answersOwnerTurn(message: MeetingRoomRow | undefined): boolean {
  return !!message && message.role === 'ASSISTANT' && !message.spokenAloud && !message.meetingApproval && !!message.content?.trim();
}
